import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface JobError {
  code: string;
  message: string;
  stage: string;
}

export interface JobStatus {
  id: string;
  status: 'queued' | 'preprocessing' | 'rendering' | 'uploading' | 'completed' | 'failed';
  progress: number;
  outputVideoUrl?: string;
  outputPath?: string;
  /** HTTP URL usable directly in a <video> element (emulator or signed URL). */
  previewVideoUrl?: string;
  /** Alias for previewVideoUrl – used by GenerationProgress / GeneratePage. */
  outputVideoPublicUrl?: string;
  error?: JobError | string;
  createdAt?: Date;
  startedAt?: Date;
  completedAt?: Date;
  updatedAt?: Date;
  retryCount?: number;
  metadata?: Record<string, unknown>;
}

export function useJobStatus(jobId: string | null) {
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      doc(db, 'jobs', jobId),
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setJobStatus({
            id: snapshot.id,
            status: data.status,
            progress: data.progress || 0,
            outputVideoUrl: data.outputVideoUrl,
            outputPath: data.outputPath,
            previewVideoUrl: data.previewVideoUrl,
            outputVideoPublicUrl: data.previewVideoUrl,
            error: data.error,
            createdAt: data.createdAt?.toDate(),
            startedAt: data.startedAt?.toDate(),
            completedAt: data.completedAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
            retryCount: data.retryCount,
            metadata: data.metadata,
          });
        } else {
          setError('Job not found');
        }
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [jobId]);

  return {
    jobStatus,
    loading,
    error,
  };
}
